import PropTypes from 'prop-types'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { calculateAge, genderLabel } from '../../../utility/userHelper'

import StudentWithAvatar from '../../students/StudentWithAvatar'
import ActionSubmittedModal from '../Modal/ActionSubmittedModal'
import RosterLabels from './RosterLabels'
import * as eventActions from '../../../actions/eventActions'
import { UncontrolledPopover } from 'reactstrap'
import colors from '../../../assets/styles/globals.scss'
import styles from './GuestList.module.scss'
import { ReactComponent as GolferIcon } from '../../../assets/images/common/icons/golfer-half-icon.svg'
import { ReactComponent as MenuIcon } from '../../../assets/images/common/icons/more-icon-vertical.svg'
import { ReactComponent as MessageIcon } from '../../../assets/images/common/icons/Message.svg'
import classNames from 'classnames'
import SeriesEnrollmentStatusSelect from './SeriesEnrollmentStatusSelect'
import Tooltip from '../../common/Tooltip'
import SubmitButton from '../../common/buttons/SubmitButton'

const GuestList = ({ event, removeStudent }) => {
  const dispatch = useDispatch()
  const students = event.students
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [showSentReminderModal, setShowSentReminderModal] = useState(false)

  const CloseReminderModalButton = (
    <SubmitButton
      onClick={() => setShowSentReminderModal(false)}
      buttonMessage='Close'
      buttonColor='navy-button'
    />
  )

  const handleStudentMenuClick = (student) => {
    setSelectedStudent(student)
  }

  const sendRSVP = () => {
    dispatch(eventActions.sendEventReminder({ eventId: event.id, userId: selectedStudent.id })).unwrap()
      .then(() => setShowSentReminderModal(true))
      .catch(() => {
        toast.error(`The rsvp reminder for "${selectedStudent.firstName + ' ' + selectedStudent.lastName}" could not be sent.`,
          { position: toast.POSITION.BOTTOM_LEFT })
      })
  }

  const handleRSVPToggle = () => {
    dispatch(eventActions.updateRsvpStatus({ id: event.id, rsvpStatus: event.rsvpStatus }))
  }

  return (
    <div id='LogScores' className='d-flex flex-column table-container'>
      <RosterLabels event={event} handleRSVPToggle={handleRSVPToggle}/>
      <div className={classNames('d-flex flex-column', styles.enrollmentsList)}>
        <Tooltip/>
        {students?.length > 0 ?
          <table className='table table-responsive animated fadeIn w-100'>
            <thead>
              <tr>
                <th className='golfer-header'>Golfer</th>
                <th className='px-5'>Age, Gender</th>
                <th>Program</th>
                <th>RSVP</th>
                <th>Pairing Status</th>
                <th>Note</th>
                <th className='centered-text'>Record Score</th>
                <th className='centered-text'>Level Attempts</th>
                <th className='px-5 centered-text'>{event.status !== 'completed' && 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              { students.map((std, index) =>
                <tr
                  key={index}
                  className={'students-table-row'}
                >
                  <td style={{ width: '25%' }} className='golfer-data'>
                    <StudentWithAvatar student={std} withName />
                  </td>
                  <td style={{ width: '19%' }} className='px-5'>{calculateAge(std.birthdate)} yrs, {genderLabel[std.gender]}</td>
                  <td style={{ width: '17%' }}>{std.courseName || 'Manually Added'}</td>
                  <td style={{ width: '13%' }}><SeriesEnrollmentStatusSelect isDisabled={event.status === 'completed'} enrollment={std}/></td>
                  <td style={{ width: '8%' }}>
                    {
                      std.pairingGroupId ?
                        <span className={styles.paired}>Paired</span> :
                        <span className={styles.notPaired}>Not Paired</span>
                    }
                  </td>
                  <td className='centered-text'>{std.note &&
                    <MessageIcon data-tip={std.note} data-tip-disable={false} className={styles.messageIcon}/>}
                  </td>
                  <td style={{ width: '10%' }} className='centered-text'>{std.stats && std.stats[std.level].recordScore || '-'}</td>
                  <td style={{ width: '10%' }} className='centered-text'>{std.stats && std.stats[std.level].levelAttempts || '-'}</td>
                  <td className='centered-text'>
                    <div className={styles.deleteButton} onClick={() => handleStudentMenuClick(std)} aria-hidden='true' id='studentMenuButton'>
                      <MenuIcon fill={colors.primaryNavy} hidden={event.status === 'completed'}/>
                    </div>
                  </td>
                </tr>
              )}

              <UncontrolledPopover
                placement='bottom'
                target='studentMenuButton'
                trigger='legacy'
              >
                <div className={classNames('d-flex flex-column', styles.studentActions)}>
                  <p aria-hidden='true' onClick={() => removeStudent(selectedStudent.enrollmentId)}>Remove Golfer</p>
                  <p aria-hidden='true' onClick={sendRSVP}>Send RSVP Reminder</p>
                </div>
              </UncontrolledPopover>
            </tbody>
          </table>
          :
          <div className={styles.noStudentsPlaceholder}>
            <GolferIcon id='golferIcon' fill={colors.lightNavy} width={'64px'} height={'64px'}/>
            <p className={styles.placeholderTitle}>No Students Enrolled</p>
            <div className={styles.placeholderDescription}>There are no enrollments for this event.</div>
          </div>
        }
      </div>
      <ActionSubmittedModal
        showModal={showSentReminderModal}
        closeModal={() => setShowSentReminderModal(false)}
        firstButton={CloseReminderModalButton}
        title='RSVP Reminder Sent!'
        icon={<i className='fa fa-envelope'/>}
      />
    </div>
  )
}

GuestList.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.number,
    students: PropTypes.array,
    status: PropTypes.string,
    rsvpStatus: PropTypes.string,
  }),
  removeStudent: PropTypes.func.isRequired,
}

export default GuestList
